import React from 'react';
import { Avatar, Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Timeline } from '@mui/icons-material';
import { format } from 'date-fns';
import { STATUS_CONFIG } from '../constants';
import { getStatus } from '../utils';

/**
 * Compact user card shown in the map's right-hand sidebar list.
 *
 * Props:
 *   row        – activity row object
 *   onClick    – handler when the card is clicked
 *   isSelected – whether this row is currently selected
 *   hasTrail   – whether a location trail is loaded for this user
 */
export default function UserCard({ row, onClick, isSelected, hasTrail }) {
  const status    = getStatus(row);
  const statusCfg = STATUS_CONFIG[status];
  const { author, checkin } = row;

  const time = checkin && checkin !== 'NA' && checkin.timeReported
    ? format(new Date(checkin.timeReported), 'hh:mm a')
    : null;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        p: 1.5, borderRadius: '12px', cursor: 'pointer',
        background: isSelected ? alpha('#1075BA', 0.07) : 'transparent',
        border: `1px solid ${isSelected ? alpha('#1075BA', 0.3) : 'transparent'}`,
        transition: 'all 0.15s ease',
        '&:hover': {
          background: alpha('#1075BA', 0.05),
          border: `1px solid ${alpha('#1075BA', 0.15)}`,
        },
      }}
    >
      {/* Avatar with status dot */}
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <Avatar
          src={author?.photoURL}
          sx={{ width: 40, height: 40, border: `2px solid ${statusCfg.color}`, fontSize: '0.85rem' }}
        >
          {author?.firstName?.[0]}
        </Avatar>
        <Box sx={{
          position: 'absolute', bottom: 0, right: 0,
          width: 10, height: 10, borderRadius: '50%',
          background: statusCfg.color, border: '1.5px solid #fff',
        }} />
      </Box>

      {/* Name + role */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: '0.83rem', fontWeight: 600, color: '#0f2137',
          lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {author?.firstName} {author?.lastName}
        </Typography>
        <Typography sx={{ fontSize: '0.72rem', color: '#4a6a85' }}>
          {author?.role?.title || author?.username}
        </Typography>
      </Box>

      {/* Status badge + time */}
      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
        {isSelected && hasTrail && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end', mb: 0.25 }}>
            <Timeline sx={{ fontSize: 12, color: '#f59e0b' }} />
            <Typography sx={{ fontSize: '0.65rem', color: '#f59e0b', fontWeight: 700 }}>Trail</Typography>
          </Box>
        )}
        <Box sx={{
          fontSize: '0.65rem', fontWeight: 700, color: statusCfg.color,
          background: statusCfg.bg, px: 0.75, py: 0.25, borderRadius: '6px',
          border: `1px solid ${statusCfg.border}`, mb: 0.25, whiteSpace: 'nowrap',
        }}>
          {statusCfg.label}
        </Box>
        {time && (
          <Typography sx={{ fontSize: '0.68rem', color: '#6b7f8e' }}>{time}</Typography>
        )}
      </Box>
    </Box>
  );
}
